"use client";

import * as React from "react";
import Link from "next/link";
import Image from "next/image";
import { createPortal } from "react-dom";
import { usePathname } from "next/navigation";
import { useTheme } from "next-themes";
import { Moon, Sun, ChevronDown, ChevronRight } from "lucide-react";
import { useUser } from "@clerk/nextjs";
import {
  NavBadgePill,
  NavPingDot,
  NavStaticDot,
} from "./nav-badges";
import type { NavSection, NavSubLink } from "../types/navigation";
import { cn } from "@workspace/ui/lib/utils";

/**
 * 좌측 사이드바 1열 — 아이콘 레일 (JCG-2060 토대 / JCG-2067 호버 확장 + 플라이아웃).
 *
 * 구조:
 *  - 항상 보이는 58px 레일(로고 + 전역 섹션 아이콘 + 로그인 사용자 내 메뉴 아이콘 + 테마 토글 + 내 정보).
 *  - 레일/드로어에 호버하면 portal(document.body)로 그린 확장 드로어(아이콘 + 라벨)가
 *    레일 위에 겹쳐 나타난다. 좌측 aside 가 overflow-hidden 이라 옆으로 펼치는 패널이
 *    잘리므로 createPortal 로 stacking context 를 탈출한다(JCG-2063 ImageInsertModal 패턴).
 *  - 레일 아이콘이나 드로어의 섹션 행을 클릭하면(또는 Enter/Space) 하위 링크 플라이아웃이 오른쪽에
 *    펼쳐진다(클릭 토글 + pin, Esc/외부클릭으로 닫힘). 호버로는 펼치지 않는다.
 *
 * 데이터는 `src/lib/nav-sections.ts`(전역 섹션)와 `src/app/my/my-nav.ts`(`/my` 탭) SSOT만 소비한다.
 */

const DRAWER_W = 232;
const CLOSE_DELAY = 160;
/**
 * 레일 밖(body 포털)에 뜨지만 레일과 한 몸으로 취급해야 하는 플로팅 UI.
 * 외부 pointerdown 닫기·마우스 이탈 타이머가 이 셀렉터에 걸리는 대상은 "레일 안"으로 본다 —
 * 그러지 않으면 드로어가 언마운트되며 트리거·팝오버가 함께 사라져 클릭이 도달하지 못한다(JCG-2154 와 같은 메커니즘).
 *   - Clerk UserButton 팝오버·모달(계정 슬롯)
 *   - 앱 슬롯이 띄우는 팝오버: 콘텐츠 루트에 `data-rail-floating-ui` 를 단다(예: 알림 벨, JCG-3601)
 */
const RAIL_FLOATING_UI_SELECTOR = [
  "[class*='cl-userButtonPopover']",
  "[class*='cl-modal']",
  "[data-rail-floating-ui]",
].join(", ");

/** active 섹션/링크 좌측 앰버 인디케이터 (레일/드로어/내메뉴 공통) */
const activeIndicatorCls =
  "absolute -left-2 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-full bg-amber-500";

/**
 * 접힌 레일 아이콘 우상단 도트 — 종류는 `NavSection.dot` SSOT 가 정한다(menu-grid.md §4-5).
 * 데스크톱 상단 Menu의 인라인 도트와는 별도이며, decorative 의미는 드로어/플라이아웃 pill 이 전달한다.
 */
function NavSectionRailDot({ dot }: { dot: NonNullable<NavSection["dot"]> }) {
  const positionCls = "absolute top-1.5 right-1.5 h-2 w-2";
  if (dot === "ping" || dot === "ping-red") {
    return (
      <NavPingDot
        className={positionCls}
        tone={dot === "ping-red" ? "red" : "amber"}
      />
    );
  }

  return <NavStaticDot className={positionCls} />;
}

function isRailFloatingUiTarget(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    Boolean(target.closest(RAIL_FLOATING_UI_SELECTOR))
  );
}

function hasVisibleRailFloatingUi(): boolean {
  if (typeof document === "undefined") return false;

  return Array.from(
    document.querySelectorAll<HTMLElement>(RAIL_FLOATING_UI_SELECTOR),
  ).some((element) => {
    // Radix 팝오버는 닫힌 뒤에도 exit 애니메이션 동안 `data-state="closed"` 로 DOM 에 남는다 — 닫힌 것으로 본다
    if (element.dataset.state === "closed") return false;
    const style = window.getComputedStyle(element);
    return (
      style.display !== "none" &&
      style.visibility !== "hidden" &&
      element.getClientRects().length > 0
    );
  });
}

/**
 * 슬롯(알림 벨 등)이 다른 곳으로 이동하기 전에 드로어·플라이아웃을 닫을 수 있게 하는 컨텍스트(JCG-3601).
 * 드로어 안의 링크는 `onClick={closeAll}` 을 직접 받지만 슬롯은 불투명한 노드라 같은 계약을 컨텍스트로 준다.
 * 레일 밖(클래식 NAV 등)에서는 no-op.
 */
const IconRailCloseContext = React.createContext<() => void>(() => {});

/** 레일 슬롯 안에서 이동 전에 호출 — 드로어·플라이아웃을 닫는다. 레일 밖에서는 아무 일도 하지 않는다. */
export function useIconRailClose(): () => void {
  return React.useContext(IconRailCloseContext);
}

/** 하위 링크가 현재 경로와 정확/접두 일치하는지 */
function isSubLinkActive(pathname: string, href?: string): boolean {
  if (!href) return false;
  if (href.startsWith("http")) return false;
  return pathname === href || pathname.startsWith(href + "/");
}

function getSubLinkKey(item: NavSubLink): string {
  return item.href ?? item.label;
}

function getSubMenuId(sectionKey: string, itemKey: string): string {
  return `rail-submenu-${sectionKey}-${encodeURIComponent(itemKey)}`;
}

function isNavSubLinkActive(pathname: string, item: NavSubLink): boolean {
  return (
    isSubLinkActive(pathname, item.href) ||
    Boolean(item.children?.some((child) => isNavSubLinkActive(pathname, child)))
  );
}

/** 내 메뉴 한 항목. 앱의 대시보드 탭 정의(JCG `MyNavItem` 등)와 같은 모양이다. */
export interface IconRailMyMenuItem {
  href: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

/** 로그인 사용자 "내 메뉴" 구성. 앱이 넘기지 않으면 해당 영역 자체를 렌더하지 않는다. */
export interface IconRailMyMenu {
  title: string;
  homeHref: string;
  homeLabel: string;
  homeIcon: React.ComponentType<{ className?: string }>;
  items: readonly IconRailMyMenuItem[];
  isItemActive: (pathname: string, href: string) => boolean;
}

export interface IconRailProps {
  appName?: string;
  logoSrc?: string;
  /**
   * 로고 이미지 대체 슬롯(레일·드로어 공용). 넘기면 `logoSrc` 대신 렌더한다.
   * 시즌 스킨처럼 CSS 로 이미지를 바꿔 끼울 때 쓴다. 32px 원형 자리에 맞춰 넘긴다.
   */
  logo?: React.ReactNode;
  /** 앱별 내비 섹션. 아이콘·라벨·경로가 여기 담긴다. */
  sections: readonly NavSection[];
  /** 섹션 활성 판정. 앱마다 경로 규칙이 다르다. */
  isSectionActive: (pathname: string, match: string[]) => boolean;
  /**
   * 내 메뉴. 넘기지 않으면 렌더하지 않는다 —
   * 해당 라우트가 없는 앱에서 죽은 링크가 노출되지 않게 하는 기본값이다.
   */
  myMenu?: IconRailMyMenu;
  /** 하단 계정 영역 슬롯. 앱의 인증 UI를 주입한다. */
  authSection?: React.ReactNode;
  /**
   * 계정 영역 바로 위에 두는 알림 슬롯(벨 등). 넘기지 않으면 렌더하지 않는다 —
   * 알림 채널이 없는 앱에서 죽은 UI 가 노출되지 않게 하는 기본값이다(JCG-3601).
   * 슬롯이 body 포털로 팝오버를 띄우면 그 콘텐츠 루트에 `data-rail-floating-ui` 를 달아야
   * 드로어 상태에서 외부 클릭 닫기에 걸려 언마운트되지 않는다(`RAIL_FLOATING_UI_SELECTOR`).
   */
  notifications?: React.ReactNode;
  /** Central settings entry; defaults to the existing theme control. */
  settingsControl?: (expanded: boolean) => React.ReactNode;
  /** 플라이아웃 하위 항목에 시한 뱃지를 입히는 앱별 훅. 없으면 원본을 그대로 쓴다. */
  decorateSubLink?: (item: NavSubLink) => NavSubLink;
}

export default function IconRail({
  appName = "정처기 감자",
  logoSrc = "/logo.webp",
  logo,
  sections,
  isSectionActive,
  myMenu,
  authSection,
  notifications,
  settingsControl,
  decorateSubLink,
}: IconRailProps) {
  const pathname = usePathname() ?? "";
  // 레일·드로어가 같은 로고를 쓴다
  const logoNode = logo ?? (
    <Image
      src={logoSrc}
      alt={appName}
      width={68}
      height={68}
      className="h-8 w-8 rounded-full object-contain"
    />
  );
  const { isSignedIn } = useUser();
  const { theme, setTheme, resolvedTheme } = useTheme();
  // useSyncExternalStore로 SSR/CSR mismatch 없이 mount 여부 판단 (set-state-in-effect 회피)
  const mounted = React.useSyncExternalStore(
    () => () => {},
    () => true,
    () => false,
  );


  const dark = mounted && (resolvedTheme ?? theme) === "dark";
  const toggleTheme = () => setTheme(dark ? "light" : "dark");
  const showMyRailItems = myMenu && mounted && isSignedIn;

  // 드로어(호버 확장) 열림 여부
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  // 현재 펼친 플라이아웃 섹션 key (하나만 열림). null 이면 닫힘.
  const [activeKey, setActiveKey] = React.useState<string | null>(null);
  // 클릭/Enter 로 pin 된 섹션 key — 마우스가 떠나도 유지됨.
  const [pinnedKey, setPinnedKey] = React.useState<string | null>(null);
  const [expandedSubMenuKeys, setExpandedSubMenuKeys] = React.useState<
    Record<string, boolean>
  >({});

  const closeTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  // 드로어가 닫혀도 남아 있는 레일 링크 ref (Esc 시 포커스 복귀용)
  const triggerRefs = React.useRef<Record<string, HTMLAnchorElement | null>>({});
  const drawerTriggerRefs = React.useRef<Record<string, HTMLButtonElement | null>>({});

  const clearCloseTimer = React.useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const openDrawer = React.useCallback(() => {
    clearCloseTimer();
    setDrawerOpen(true);
  }, [clearCloseTimer]);

  const closeAll = React.useCallback(() => {
    clearCloseTimer();
    setDrawerOpen(false);
    setActiveKey(null);
    setPinnedKey(null);
  }, [clearCloseTimer]);

  // pin 된 섹션이 있으면 드로어/플라이아웃을 닫지 않는다(Esc/외부클릭으로만 닫힘).
  // pin 이 없으면 마우스 이탈 시 짧은 지연 후 전체 닫힘(드로어 ↔ 플라이아웃 이동 틈 허용).
  const handleRootLeave = React.useCallback((event: React.PointerEvent) => {
    if (event.pointerType !== "mouse") return;
    if (pinnedKey) return;
    if (isRailFloatingUiTarget(event.relatedTarget)) return;

    clearCloseTimer();
    closeTimer.current = setTimeout(() => {
      if (hasVisibleRailFloatingUi()) return;

      setDrawerOpen(false);
      setActiveKey(null);
    }, CLOSE_DELAY);
  }, [pinnedKey, clearCloseTimer]);

  // 호버로 섹션 플라이아웃 펼치기(미pin 시). pin 된 섹션이 있으면 호버로 바꾸지 않음.
  const handleSectionHover = React.useCallback(
    (key: string) => {
      clearCloseTimer();
      if (pinnedKey) return;
      setActiveKey(key);
    },
    [clearCloseTimer, pinnedKey],
  );

  // 하단(테마/내 정보) 호버 시 열린 플라이아웃만 접고 드로어는 유지(미pin 시).
  const collapseFlyout = React.useCallback(() => {
    clearCloseTimer();
    if (pinnedKey) return;
    setActiveKey(null);
  }, [clearCloseTimer, pinnedKey]);

  // 클릭/Enter 로 토글 + pin
  const handleSectionToggle = React.useCallback((key: string) => {
    clearCloseTimer();
    setDrawerOpen(true);
    const next = activeKey === key ? null : key;
    setActiveKey(next);
    setPinnedKey(next);
  }, [activeKey, clearCloseTimer]);

  const toggleSubMenu = React.useCallback((key: string) => {
    setExpandedSubMenuKeys((current) => ({
      ...current,
      [key]: !(current[key] ?? false),
    }));
  }, []);

  // Esc → 플라이아웃/드로어 닫고 트리거로 포커스 복귀. 외부 클릭 → 닫기.
  React.useEffect(() => {
    if (!drawerOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // 레일 소유 플로팅 UI(Clerk 팝오버·알림 벨 팝오버) 안에서 누른 Esc(포커스가 그 안에 있음)나 그것이 열려 있는
        // 동안의 Esc 는 그쪽이 먼저 닫히게 두고 드로어는 유지한다 — 같은 Esc 로 트리거까지 언마운트하면 포커스 복귀
        // 대상이 사라진다(JCG-3601). 대상 검사는 Radix 가 먼저 닫아 exit 애니메이션 중이어도 성립한다.
        if (isRailFloatingUiTarget(e.target) || hasVisibleRailFloatingUi()) return;
        const focusKey = activeKey ?? pinnedKey;
        closeAll();
        if (focusKey) triggerRefs.current[focusKey]?.focus();
      }
    };
    const onPointerDown = (e: PointerEvent) => {
      if (isRailFloatingUiTarget(e.target)) {
        // 플로팅 UI(Clerk 팝오버·알림 벨 팝오버) 안의 클릭은 레일 안으로 본다. 그 클릭으로 다른 곳에 가는 슬롯은
        // `useIconRailClose()` 로 스스로 드로어를 닫는다(알림 벨, JCG-3601).
        clearCloseTimer();
        return;
      }

      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        closeAll();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [drawerOpen, activeKey, pinnedKey, closeAll, clearCloseTimer]);

  React.useEffect(() => clearCloseTimer, [clearCloseTimer]);

  // 키보드로 연 경우 포커스가 포털 뒤의 접힌 레일에 가려지지 않도록 옮긴다.
  React.useEffect(() => {
    if (drawerOpen && activeKey && document.activeElement === triggerRefs.current[activeKey]) {
      drawerTriggerRefs.current[activeKey]?.focus();
    }
  }, [drawerOpen, activeKey]);

  const activeSection: NavSection | null =
    sections.find((s) => s.key === activeKey) ?? null;

  // 항상 보이는 58px 레일 (aside 안에 인라인 렌더)
  const rail = (
    <div
      // 앱이 레일 구분선 등을 CSS 로 꾸밀 때 쓰는 고정 훅(시즌 스킨 등)
      data-icon-rail
      className="flex h-full w-[58px] shrink-0 flex-col items-center border-r border-border bg-muted/40 py-3"
    >
      <div className="scrollbar-hide flex min-h-0 flex-1 flex-col items-center gap-1 overflow-x-hidden overflow-y-auto px-2">
        <div
          className="flex shrink-0 flex-col items-center gap-1"
          onPointerEnter={(event) => {
            if (event.pointerType === "mouse") openDrawer();
          }}
        >
          <Link
            href="/"
            title={appName}
            className="mb-1.5 flex h-9 w-9 shrink-0 items-center justify-center"
          >
            {logoNode}
          </Link>

          {sections.map((section) => {
            const on = isSectionActive(pathname, section.match);
            const Icon = section.icon;
            return (
              <Link
                key={section.key}
                href={section.href}
                ref={(el) => {
                  triggerRefs.current[section.key] = el;
                }}
                title={section.label}
                aria-label={section.label}
                aria-current={on ? "page" : undefined}
                aria-expanded={drawerOpen && activeKey === section.key}
                aria-controls={`rail-flyout-${section.key}`}
                onClick={(event) => {
                  // 새 탭/창 열기와 하위 항목이 없는 섹션의 링크 동작은 유지한다.
                  if (
                    event.button !== 0 || event.metaKey || event.ctrlKey ||
                    event.shiftKey || event.altKey || section.items.length === 0
                  ) return;
                  event.preventDefault();
                  handleSectionToggle(section.key);
                }}
                onKeyDown={(event) => {
                  if (
                    event.key === " " && !event.repeat &&
                    !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey &&
                    section.items.length > 0
                  ) {
                    event.preventDefault();
                    handleSectionToggle(section.key);
                  }
                }}
                className={cn(
                  "relative flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-xl transition-colors focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:outline-none",
                  on
                    ? "bg-card text-foreground shadow-sm"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground",
                )}
              >
                {on && <span className={activeIndicatorCls} />}
                <Icon
                  aria-hidden="true"
                  className={cn("h-[19px] w-[19px]", section.accent && on && "text-amber-500")}
                />
                {/* 접힌 상태에서도 보이는 신호 도트 — 모의고사·감자 마켓=amber pulse / 커뮤니티=red pulse.
                    의미는 드로어 섹션 행·플라이아웃 하위 행의 pill 이 전달한다(§4-5). */}
                {section.dot && <NavSectionRailDot dot={section.dot} />}
              </Link>
            );
          })}
        </div>

        {showMyRailItems && (
          <div
            className="mt-auto flex shrink-0 flex-col items-center gap-0.5 pt-1"
            onPointerEnter={(event) => {
              if (event.pointerType === "mouse") openDrawer();
            }}
          >
            <div
              aria-hidden="true"
              className="my-1 h-px w-7 shrink-0 bg-border"
            />
            {(() => {
              const on = pathname === myMenu.homeHref;
              const DashboardIcon = myMenu.homeIcon;
              return (
                <Link
                  href={myMenu.homeHref}
                  title={myMenu.homeLabel}
                  aria-label={myMenu.homeLabel}
                  aria-current={on ? "page" : undefined}
                  className={cn(
                    "relative flex h-[38px] w-[42px] shrink-0 items-center justify-center rounded-xl transition-colors focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:outline-none",
                    on
                      ? "bg-card text-amber-600 shadow-sm dark:text-amber-400"
                      : "text-muted-foreground hover:bg-accent hover:text-foreground",
                  )}
                >
                  {on && <span className={activeIndicatorCls} />}
                  <DashboardIcon aria-hidden="true" className="h-[19px] w-[19px]" />
                </Link>
              );
            })()}
            {myMenu.items.map((item) => {
              const on = myMenu.isItemActive(pathname, item.href);
              const Icon = item.icon;
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  title={item.label}
                  aria-label={item.label}
                  aria-current={on ? "page" : undefined}
                  className={cn(
                    "relative flex h-[38px] w-[42px] shrink-0 items-center justify-center rounded-xl transition-colors focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:outline-none",
                    on
                      ? "bg-card text-amber-600 shadow-sm dark:text-amber-400"
                      : "text-muted-foreground hover:bg-accent hover:text-foreground",
                  )}
                >
                  {on && <span className={activeIndicatorCls} />}
                  <Icon aria-hidden="true" className="h-[19px] w-[19px]" />
                </Link>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-2 flex shrink-0 flex-col items-center gap-1 px-2">
        {/* 알림 슬롯(벨) — 앱이 넘길 때만. 슬롯이 null 을 그리면(비로그인) 래퍼도 숨겨 빈 칸을 남기지 않는다 (JCG-3601) */}
        {notifications && (
          <div className="flex h-[42px] w-[42px] items-center justify-center empty:hidden">
            {notifications}
          </div>
        )}
        {settingsControl?.(false)}
        <div className="flex h-[42px] w-[42px] items-center justify-center">
          {/* 데스크탑: Clerk 기본 항목(계정 관리·로그아웃)만 노출 (JCG-2160) */}
          {authSection}
        </div>
        {!settingsControl && (
        <button
          type="button"
          title="테마 변경"
          onClick={toggleTheme}
          className="flex h-[42px] w-[42px] items-center justify-center rounded-xl text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:outline-none"
        >
          {dark ? <Sun aria-hidden="true" className="h-[18px] w-[18px]" /> : <Moon aria-hidden="true" className="h-[18px] w-[18px]" />}
          <span className="sr-only">테마 변경</span>
        </button>
        )}
      </div>
    </div>
  );

  // 호버 확장 드로어 + 플라이아웃 (portal 로 document.body 에 그려 aside overflow-hidden 탈출).
  // 바깥 컨테이너는 pointer-events-none 로 클릭을 통과시키고, 드로어/플라이아웃만 pointer 활성화.
  const overlay =
    mounted && drawerOpen && typeof document !== "undefined"
      ? createPortal(
          <div
            ref={rootRef}
            className="fixed inset-y-0 left-0 z-50 flex pointer-events-none"
            onPointerLeave={handleRootLeave}
          >
            {/* 확장 드로어 — 동적 뷰포트(h-dvh)로 모바일/태블릿 툴바를 피해 하단 항목까지 노출(JCG-2239) */}
            <div
              className="pointer-events-auto flex h-dvh min-h-0 flex-col border-r border-border bg-background py-3 shadow-lg"
              style={{ width: DRAWER_W }}
              onPointerEnter={(event) => {
                if (event.pointerType === "mouse") openDrawer();
              }}
            >
              {/* 로고 + 라벨 (드로어 헤더, 레일 로고와 동일 위치) */}
              <Link
                href="/"
                className="mb-1.5 flex h-9 shrink-0 items-center gap-2.5 px-4"
                onClick={closeAll}
              >
                {logoNode}
                <span className="text-sm font-semibold text-foreground">
                  {appName}
                </span>
              </Link>

              <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto overscroll-contain px-3">
                <nav className="flex shrink-0 flex-col gap-0.5">
                  {sections.map((section) => {
                    const on = isSectionActive(pathname, section.match);
                    const expanded = activeKey === section.key;
                    const Icon = section.icon;
                    return (
                      <button
                        key={section.key}
                        type="button"
                        ref={(el) => {
                          drawerTriggerRefs.current[section.key] = el;
                        }}
                        aria-current={on ? "page" : undefined}
                        aria-haspopup="true"
                        aria-expanded={expanded}
                        aria-controls={`rail-flyout-${section.key}`}
                        onClick={() => handleSectionToggle(section.key)}
                        className={cn(
                          "group relative flex h-[42px] w-full items-center gap-3 rounded-xl px-3 text-left text-sm transition-colors focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:outline-none",
                          on || expanded
                            ? "bg-card text-foreground shadow-sm"
                            : "text-muted-foreground hover:bg-accent hover:text-foreground",
                        )}
                      >
                        {on && <span className={activeIndicatorCls} />}
                        <Icon
                          aria-hidden="true"
                          className={cn(
                            "h-[19px] w-[19px] shrink-0",
                            section.accent && on && "text-amber-500",
                          )}
                        />
                        <span className="flex-1 truncate font-medium">{section.label}</span>
                        {/* 섹션 강조 뱃지(파이널 새 회차 UPDATE 등) — 접힌 레일 도트와 짝을 이루는 확장 상태 신호 */}
                        {section.badge && <NavBadgePill badge={section.badge} tone={section.badgeTone} />}
                        <ChevronRight
                          aria-hidden="true"
                          className={cn(
                            "h-4 w-4 shrink-0 transition-transform",
                            expanded ? "text-foreground" : "text-muted-foreground/60",
                          )}
                        />
                      </button>
                    );
                  })}
                </nav>

                {/* 내 메뉴는 접힌 레일처럼 하단에 모으고, 낮은 화면에서는 스크롤로 접근한다.
                    대시보드(/my) 진입점 + 구분선 + 탭 항목. my/layout.tsx 좌측 탭 네비를 미러링하며
                    데이터는 myMenu prop 만 소비한다(로컬 재정의 금지). */}
                {myMenu && isSignedIn && (
                  <div
                    className="mt-auto shrink-0 border-t border-border pt-2"
                    onPointerEnter={(event) => {
                      if (event.pointerType === "mouse") collapseFlyout();
                    }}
                  >
                    <p className="px-3 pb-1 text-[11px] font-medium text-muted-foreground">
                      {myMenu.title}
                    </p>
                    <div className="flex flex-col gap-0.5">
                      {/* 대시보드(/my) 진입점 — 정확 일치일 때만 active */}
                      {(() => {
                        const on = pathname === myMenu.homeHref;
                        const DashboardIcon = myMenu.homeIcon;
                        return (
                          <Link
                            href={myMenu.homeHref}
                            aria-current={on ? "page" : undefined}
                            onClick={closeAll}
                            className={cn(
                              "relative flex h-[38px] w-full items-center gap-3 rounded-xl px-3 text-sm transition-colors focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:outline-none",
                              on
                                ? "bg-card font-medium text-amber-600 shadow-sm dark:text-amber-400"
                                : "text-muted-foreground hover:bg-accent hover:text-foreground",
                            )}
                          >
                            {on && <span className={activeIndicatorCls} />}
                            <DashboardIcon
                              aria-hidden="true"
                              className="h-[18px] w-[18px] shrink-0"
                            />
                            <span className="flex-1 truncate">
                              {myMenu.homeLabel}
                            </span>
                          </Link>
                        );
                      })()}
                      {/* 대시보드와 7개 탭 구분선 (my/layout.tsx 미러링) */}
                      <div className="my-1 border-t border-border" />
                      {myMenu.items.map((item) => {
                        const on = myMenu.isItemActive(pathname, item.href);
                        const Icon = item.icon;
                        return (
                          <Link
                            key={item.href}
                            href={item.href}
                            aria-current={on ? "page" : undefined}
                            onClick={closeAll}
                            className={cn(
                              "relative flex h-[38px] w-full items-center gap-3 rounded-xl px-3 text-sm transition-colors focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:outline-none",
                              on
                                ? "bg-card font-medium text-amber-600 shadow-sm dark:text-amber-400"
                                : "text-muted-foreground hover:bg-accent hover:text-foreground",
                            )}
                          >
                            {on && <span className={activeIndicatorCls} />}
                            <Icon aria-hidden="true" className="h-[18px] w-[18px] shrink-0" />
                            <span className="flex-1 truncate">{item.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* 하단 아이콘은 설정 행의 아이콘과 같은 42px 열 중앙에 맞춘다. */}
              <div className="mt-2 flex shrink-0 flex-col gap-0.5 px-3">
                {notifications && (
                  <div
                    className="flex h-[42px] w-[42px] items-center justify-center empty:hidden"
                    onPointerEnter={(event) => {
                      if (event.pointerType === "mouse") collapseFlyout();
                    }}
                  >
                    {notifications}
                  </div>
                )}
                {settingsControl?.(true)}
                <div
                  className="flex h-[42px] w-[42px] items-center justify-center"
                  onPointerEnter={(event) => {
                    if (event.pointerType === "mouse") collapseFlyout();
                  }}
                >
                  {/* 데스크탑: Clerk 기본 항목(계정 관리·로그아웃)만 노출 (JCG-2160) */}
                  {authSection}
                </div>
                {!settingsControl && (
                <button
                  type="button"
                  onClick={toggleTheme}
                  onPointerEnter={(event) => {
                    if (event.pointerType === "mouse") collapseFlyout();
                  }}
                  className="flex h-[42px] w-full items-center gap-3 rounded-xl px-3 text-left text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:outline-none"
                >
                  {dark ? <Sun aria-hidden="true" className="h-[18px] w-[18px] shrink-0" /> : <Moon aria-hidden="true" className="h-[18px] w-[18px] shrink-0" />}
                  <span className="font-medium">{dark ? "라이트 모드" : "다크 모드"}</span>
                </button>
                )}
              </div>
            </div>

            {/* 플라이아웃 (서브메뉴) — 활성 섹션의 하위 링크 */}
            {activeSection && activeSection.items.length > 0 && (
              <div
                id={`rail-flyout-${activeSection.key}`}
                role="menu"
                aria-label={activeSection.label}
                className="pointer-events-auto flex h-dvh min-h-0 w-[244px] flex-col border-r border-border bg-background py-3 shadow-lg"
                onPointerEnter={(event) => {
                  if (event.pointerType === "mouse") handleSectionHover(activeSection.key);
                }}
              >
                {/* 플라이아웃 헤더 = 섹션 랜딩 링크 */}
                <Link
                  href={activeSection.href}
                  onClick={closeAll}
                  className="mx-3 mb-1 flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm font-semibold text-foreground transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:outline-none"
                >
                  <activeSection.icon
                    aria-hidden="true"
                    className={cn(
                      "h-[18px] w-[18px] shrink-0",
                      activeSection.accent && "text-amber-500",
                    )}
                  />
                  <span className="truncate">{activeSection.label}</span>
                </Link>
                <div className="mx-3 mb-1 border-t border-border" />

                <div className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto overscroll-contain px-3">
                  {activeSection.items.map((staticItem) => {
                    // 시각 파생 뱃지("곧 마감")를 소비 시점에 주입 — 판매 종료 시 자동으로 꺼진다.
                    // 플라이아웃은 열기 전엔 렌더되지 않으므로 마운트 후 노출에 따른 CLS 가 없다.
                    const item = decorateSubLink ? decorateSubLink(staticItem) : staticItem;
                    const itemKey = getSubLinkKey(item);
                    const subActive = isNavSubLinkActive(pathname, item);
                    const hasChildren = Boolean(item.children?.length);
                    const subMenuExpanded = hasChildren
                      ? expandedSubMenuKeys[itemKey] ?? subActive
                      : false;
                    const subMenuId = getSubMenuId(activeSection.key, itemKey);
                    const base =
                      "flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors";
                    const highlightedSubLinkClassName =
                      "bg-amber-50 font-medium text-amber-700 hover:bg-amber-100 dark:bg-amber-950 dark:text-amber-300 dark:hover:bg-amber-900/60";
                    const content = (
                      <>
                        <span className="flex min-w-0 flex-1 items-center gap-2">
                          {item.iconSrc && (
                            <Image
                              src={item.iconSrc}
                              alt=""
                              width={24}
                              height={24}
                              aria-hidden="true"
                              className="h-6 w-6 shrink-0 rounded-full object-contain"
                            />
                          )}
                          <span className="min-w-0 flex-1 truncate">{item.label}</span>
                        </span>
                        {item.badge && <NavBadgePill badge={item.badge} tone={item.badgeTone} />}
                      </>
                    );
                    const className = cn(
                      base,
                      "focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:outline-none",
                      subActive
                        ? item.highlight
                          ? highlightedSubLinkClassName
                          : "text-amber-600 dark:text-amber-400 font-medium"
                        : item.highlight
                          ? highlightedSubLinkClassName
                          : "text-foreground hover:bg-accent",
                    );

                    if (hasChildren) {
                      return (
                        <div key={itemKey} className="space-y-0.5">
                          <button
                            type="button"
                            role="menuitem"
                            aria-expanded={subMenuExpanded}
                            aria-controls={subMenuId}
                            onClick={() => toggleSubMenu(itemKey)}
                            className={cn(className, "w-full")}
                          >
                            {content}
                            <ChevronDown
                              aria-hidden="true"
                              className={cn(
                                "h-3.5 w-3.5 shrink-0 transition-transform",
                                subMenuExpanded ? "rotate-180" : "rotate-0",
                              )}
                            />
                          </button>
                          {subMenuExpanded && (
                            <div
                              id={subMenuId}
                              className="mt-1 mb-1 space-y-0.5 rounded-lg border border-amber-200 bg-amber-50/40 p-1 dark:border-amber-800/70 dark:bg-amber-950/30"
                            >
                              {item.children?.map((child) => {
                                if (!child.href) return null;

                                const childActive = isSubLinkActive(pathname, child.href);
                                return (
                                  <Link
                                    key={child.href}
                                    href={child.href}
                                    role="menuitem"
                                    aria-current={childActive ? "page" : undefined}
                                    onClick={closeAll}
                                    className={cn(
                                      "flex items-center gap-2 rounded-md px-2.5 py-1.5 text-sm transition-colors focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:outline-none",
                                      childActive
                                        ? "bg-card font-medium text-amber-600 shadow-sm dark:text-amber-400"
                                        : "text-foreground hover:bg-accent",
                                    )}
                                  >
                                    {child.iconSrc && (
                                      <Image
                                        src={child.iconSrc}
                                        alt=""
                                        width={24}
                                        height={24}
                                        aria-hidden="true"
                                        className="h-6 w-6 shrink-0 rounded-full object-contain"
                                      />
                                    )}
                                    <span className="min-w-0 flex-1 truncate">{child.label}</span>
                                  </Link>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    }

                    if (!item.href) return null;

                    return item.external ? (
                      <a
                        key={itemKey}
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        role="menuitem"
                        onClick={closeAll}
                        className={className}
                      >
                        {content}
                      </a>
                    ) : (
                      <Link
                        key={itemKey}
                        href={item.href}
                        role="menuitem"
                        aria-current={subActive ? "page" : undefined}
                        onClick={closeAll}
                        className={className}
                      >
                        {content}
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>,
          document.body,
        )
      : null;

  return (
    <IconRailCloseContext.Provider value={closeAll}>
      {rail}
      {overlay}
    </IconRailCloseContext.Provider>
  );
}
